import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { institucionalApi } from '../../api/endpoints'
import { Header } from './Header'
import { Footer } from './Footer'
import { CookieBanner } from './CookieBanner'
import { css } from './css'

const TUTORIAL_CARD = css('background: #131B2C; border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; padding: 20px; display: flex; flex-direction: column; gap: 10px;')
const TUTORIAL_ICONE = css('width: 36px; height: 36px; border-radius: 9px; background: rgba(245,166,35,0.12); display: flex; align-items: center; justify-content: center; color: #F5A623;')
const TUTORIAL_TITULO = css('color: #FFFFFF; font-size: 14px; font-weight: 700;')
const TUTORIAL_TEXTO = css('color: #8892A8; font-size: 13px; line-height: 1.5;')

const TUTORIAIS: { titulo: string; texto: string; icone: React.ReactNode }[] = [
  { titulo: 'Criar sua primeira conta', texto: 'Cadastre contas correntes, poupanças ou carteiras e comece a lançar suas movimentações.', icone: <path d="M12 5v14M5 12h14" /> },
  { titulo: 'Lançar uma compra no cartão', texto: 'Registre compras à vista ou parceladas e acompanhe a fatura sendo calculada automaticamente.', icone: <><rect x={2.5} y={5} width={19} height={14} rx={2.5} /><path d="M2.5 10h19" /><path d="M6 15h4" /></> },
  { titulo: 'Conectar seu banco automaticamente', texto: 'Use o Open Finance para importar lançamentos sem digitar nada, com a Pluggy.', icone: <><path d="M20 11A8 8 0 0 0 6.3 6.3L3 9.6" /><path d="M4 13a8 8 0 0 0 13.7 4.7L21 14.4" /><path d="M3 5v4.6h4.6M21 19v-4.6h-4.6" /></> },
  { titulo: 'Compartilhar com a família', texto: 'Convide outra pessoa e defina o que ela pode ver ou editar na sua conta.', icone: <><circle cx={9} cy={8} r={3.2} /><path d="M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" /><circle cx={17.5} cy={9} r={2.6} /><path d="M15.7 14.6c2.6.3 4.3 2.2 4.3 5.4" /></> },
  { titulo: 'Configurar orçamentos e alertas', texto: 'Defina limites por categoria e receba um aviso antes de estourar o orçamento do mês.', icone: <path d="M4 19V10M10 19V5M16 19v-7M22 19H2" /> },
  { titulo: 'Ativar notificações', texto: 'Saiba na hora quando uma conta conectada é atualizada, no site ou no aplicativo.', icone: <><path d="M6 10a6 6 0 1 1 12 0c0 4 1.5 5.5 1.5 5.5H4.5S6 14 6 10Z" /><path d="M9.5 18.5a2.5 2.5 0 0 0 5 0" /></> },
]

export function DuvidasPage() {
  const [abertos, setAbertos] = useState<Record<string, boolean>>({})
  const { data: perguntas = [], isLoading } = useQuery({ queryKey: ['faq-publico'], queryFn: institucionalApi.faq })

  return (
    <div style={css('width: 100%; min-height: 100vh; background: #0B1220; display: flex; flex-direction: column;')}>
      <Header atual="duvidas" />

      <div style={css('max-width: 900px; margin: 0 auto; width: 100%; box-sizing: border-box; padding: 72px 32px 32px; text-align: center; display: flex; flex-direction: column; gap: 12px;')}>
        <span style={css('color: #F5A623; font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px;')}>Central de ajuda</span>
        <h1 style={css('margin: 0; color: #FFFFFF; font-size: 34px; font-weight: 800;')}>Dúvidas e tutoriais</h1>
        <p style={css('margin: 0; color: #A8B0C3; font-size: 15px;')}>Guias rápidos para tirar o máximo do Walleon e respostas para as perguntas mais comuns.</p>
      </div>

      <div style={css('max-width: 1000px; margin: 0 auto; width: 100%; box-sizing: border-box; padding: 24px 32px 64px;')}>
        <h2 style={css('color: #FFFFFF; font-size: 18px; font-weight: 700; margin: 0 0 18px;')}>Tutoriais</h2>
        <div style={css('display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px;')}>
          {TUTORIAIS.map((t) => (
            <div key={t.titulo} style={TUTORIAL_CARD}>
              <div style={TUTORIAL_ICONE}>
                <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">{t.icone}</svg>
              </div>
              <div style={TUTORIAL_TITULO}>{t.titulo}</div>
              <div style={TUTORIAL_TEXTO}>{t.texto}</div>
            </div>
          ))}
        </div>
      </div>

      {(isLoading || perguntas.length > 0) && (
        <div style={css('max-width: 800px; margin: 0 auto; width: 100%; box-sizing: border-box; padding: 0 32px 96px;')}>
          <h2 style={css('color: #FFFFFF; font-size: 18px; font-weight: 700; margin: 0 0 18px;')}>Perguntas frequentes</h2>
          <div style={css('border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; overflow: hidden;')}>
            {perguntas.map((p) => {
              const aberto = !!abertos[p.id]
              return (
                <div key={p.id} style={css('border-bottom: 1px solid rgba(255,255,255,0.06);')}>
                  <button
                    onClick={() => setAbertos((a) => ({ ...a, [p.id]: !a[p.id] }))}
                    style={css('display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 18px 22px; cursor: pointer; background: #131B2C; border: none; width: 100%; text-align: left;')}
                  >
                    <span style={css('color: #FFFFFF; font-size: 14px; font-weight: 600;')}>{p.pergunta}</span>
                    <span style={css('color: #F5A623; font-size: 18px; font-weight: 700; flex-shrink: 0;')}>{aberto ? '–' : '+'}</span>
                  </button>
                  {aberto && (
                    <div style={css('padding: 4px 22px 20px; background: #131B2C;')}>
                      <p style={css('margin: 0; color: #A8B0C3; font-size: 14px; line-height: 1.7; white-space: pre-wrap;')}>{p.resposta}</p>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      <Footer />
      <CookieBanner />
    </div>
  )
}
